import { useEffect } from 'react';
import { Button } from '@safetyhub/ui';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { usePermissions } from '../../hooks/usePermissions';
import { sitesApi, SiteAssignment, extractApiError } from '../../lib/organizations';
import { useToastStore } from '../../stores/toast';
import { ChevronLeftIcon } from '../../components/icons';
import { useSiteFormState } from '../../hooks/useSiteFormState';
import { SiteFormBody } from '../../components/sites/SiteFormBody';
import { agencyLinkRequestPath } from '../../lib/agencySiteLinkView';

interface SiteFormProps {
  isEdit?: boolean;
}

export default function SiteForm({ isEdit = false }: SiteFormProps) {
  const navigate = useNavigate();
  const { siteId } = useParams<{ siteId: string }>();
  const { currentMembership } = useAuthStore();
  const companyId = currentMembership?.companyId;
  const memberId = currentMembership?.id;
  // 생성 = `site.manage`(컨트롤러 가드와 같은 키), 수정 = `site.manage` ∥ 정 담당자(서버 update와 같은 OR) — P4 ②
  const { canManageSites } = usePermissions();
  const { showToast } = useToastStore();

  const state = useSiteFormState({
    companyId,
    isEdit,
    siteId,
    onSaved: () => {
      navigate(isEdit && siteId ? `/sites/${siteId}` : '/sites');
    },
    // P4 — 업체 승인 대기로 끝났다. 현장이 아직 없으니 상세가 아니라 요청 화면으로 간다.
    onRequested: (result) => {
      navigate(agencyLinkRequestPath(result.requestId));
    },
  });

  useEffect(() => {
    if (!isEdit && !canManageSites) {
      navigate('/sites');
    }
  }, [isEdit, canManageSites, navigate]);

  // 수정 모드: 현장 담당자 권한 체크 (useSiteFormState가 기본 로드는 담당하지만
  // Primary Assignee 권한은 여기서 별도 확인 후 차단)
  useEffect(() => {
    const checkPermission = async () => {
      if (!isEdit || !siteId || !companyId) return;
      try {
        const assignments = await sitesApi.getAssignments(companyId, siteId);
        const isPrimaryAssignee = assignments.some(
          (a: SiteAssignment) =>
            a.memberId === memberId && a.assignmentType === 'PRIMARY',
        );
        const hasEditPermission = canManageSites || isPrimaryAssignee;
        if (!hasEditPermission) {
          showToast('현장 수정 권한이 없습니다.', 'error');
          navigate(`/sites/${siteId}`);
        }
      } catch (err) {
        const apiError = extractApiError(err);
        showToast(apiError.message, 'error');
      }
    };
    void checkPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, siteId, companyId, memberId, canManageSites]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void state.performSave(false);
  };

  if (state.isFetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/sites')}
          className="flex items-center text-ink-600 hover:text-ink-900 mb-4"
        >
          <ChevronLeftIcon className="h-5 w-5 mr-1" />
          현장 목록으로
        </button>
        <h1 className="text-2xl font-bold text-ink-900">
          {isEdit ? '현장 수정' : '새 현장 추가'}
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          {isEdit
            ? '현장 정보를 수정합니다.'
            : '새로운 현장을 등록합니다. 현장별로 점검과 보고서를 관리할 수 있습니다.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <SiteFormBody state={state} isEdit={isEdit} compact={false} />

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="secondary" onClick={() => navigate('/sites')} disabled={state.isLoading}>
            취소
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={state.isLoading || !state.canSubmit}
          >
            {state.isLoading
              ? isEdit
                ? '저장 중...'
                : '생성 중...'
              : isEdit
                ? '저장'
                : '현장 추가'}
          </Button>
        </div>
      </form>
    </div>
  );
}
